/*
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */
import i18n from "../i18n/index.js";

const t = i18n.global.t;

/**
 * IconeService provides methods to retrieve icons and labels for
 * items (files or folders) based on their MIME types or node types.
 */
class IconeService {
  getIcon(item) {
    let icon;
    if (item.isFile) {
      switch (item.content.mimeType) {
        case "application/vnd.oasis.opendocument.text":
        case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        case "application/msword":
        case "application/rtf":
          icon = "collabora-text";
          break;
        case "application/vnd.ms-excel":
        case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
        case "application/vnd.oasis.opendocument.spreadsheet":
          icon = "collabora-sheet";
          break;
        case "application/vnd.oasis.opendocument.graphics":
          icon = "collabora-draw";
          break;
        case "application/vnd.oasis.opendocument.presentation":
        case "application/vnd.ms-powerpoint":
        case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
          icon = "collabora-presentation";
          break;

        case "application/eps":
          icon = "image-ai";
          break;
        case "application/epub+zip":
          icon = "file-epub";
          break;
        case "application/json":
          icon = "text-json";
          break;
        case "application/pdf":
          icon = "file-pdf";
          break;
        case "application/x-javascript":
          icon = "text-js";
          break;
        case "application/pkcs7-signature":
          icon = "pkcs7";
          break;
        case "application/x-pkcs12":
          icon = "pkcs12";
          break;
        case "application/x-x509-ca-cert":
          icon = "x509";
          break;
        case "application/x-sh":
          icon = "text-sh";
          break;
        case "application/zip":
        case "application/x-gzip":
          icon = "file-zip";
          break;
        case "application/x-7z-compressed":
          icon = "file-7z";
          break;
        case "application/x-rar-compressed":
          icon = "file-rar";
          break;
        case "application/x-tar":
          icon = "file-tar";
          break;

        case "audio/x-flac":
          icon = "audio-flac";
          break;
        case "audio/mp4":
          icon = "audio-mp4";
          break;
        case "audio/mpeg":
          icon = "audio-mp3";
          break;
        case "audio/ogg":
          icon = "audio-ogg";
          break;
        case "audio/x-wav":
          icon = "audio-wav";
          break;

        case "image/bmp":
          icon = "image-bmp";
          break;
        case "image/gif":
          icon = "image-gif";
          break;
        case "image/jpeg":
          icon = "image-jpg";
          break;
        case "image/png":
          icon = "image-png";
          break;
        case "image/svg+xml":
          icon = "image-svg";
          break;
        case "image/tiff":
          icon = "image-tiff";
          break;
        case "image/webp":
          icon = "image-webp";
          break;
        case "image/x-xcf":
          icon = "image-xfc";
          break;

        case "message/rfc822":
          icon = "file-eml";
          break;

        case "text/css":
          icon = "text-css";
          break;
        case "text/csv":
          icon = "text-csv";
          break;
        case "text/html":
          icon = "text-html";
          break;
        case "text/markdown":
        case "text/x-markdown":
        case "text/x-web-markdown":
          icon = "text-md";
          break;
        case "text/plain":
          icon = "text-plain";
          break;
        case "text/sql":
          icon = "text-sql";
          break;
        case "text/xml":
        case "application/xml":
          icon = "text-xml";
          break;

        case "video/avi":
          icon = "video-avi";
          break;
        case "video/mp4":
        case "video/x-m4v":
          icon = "video-mp4";
          break;
        case "video/mpeg":
        case "video/x-mpeg":
          icon = "video-mpeg";
          break;
        case "video/ogg":
          icon = "video-ogg";
          break;
        case "video/webm":
          icon = "video-webm";
          break;

        default:
          icon = "file-unknow";
      }

      if (icon === "file-unknow") {
        if (item.content.mimeType.startsWith("image/")) {
          icon = "image-unknow";
        } else if (item.content.mimeType.startsWith("video/")) {
          icon = "video-unknow";
        } else if (item.content.mimeType.startsWith("audio/")) {
          icon = "audio-unknow";
        } else if (item.content.mimeType.startsWith("text/")) {
          icon = "text-unknow";
        } else if (item.content.mimeType.startsWith("application/x-font-")) {
          icon = "file-font";
        } else if (item.content.mimeType.startsWith("font/")) {
          icon = "file-font";
        }
      }
    } else {
      switch (item.nodeType) {
        case "st:site":
          icon = "mimetype_espace";
          break;
        default:
          icon = "mimetype_folder";
      }
    }

    return icon;
  }

  getIconLabel(item) {
    let label;
    if (item.isFolder) {
      label = t("dossier");
    } else if (item.isFile) {
      if (item.content.mimeType.startsWith("image/")) {
        label = t("fichier_image");
      } else if (item.content.mimeType.startsWith("video/")) {
        label = t("fichier_video");
      } else {
        switch (item.content.mimeType) {
          case "application/pdf":
            label = t("fichier_pdf");
            break;
          case "message/rfc822":
            label = t("fichier_eml");
            break;
          case "application/vnd.oasis.opendocument.text":
          case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
          case "application/msword":
          case "application/rtf":
            label = t("fichier_texte");
            break;
          case "application/vnd.ms-excel":
          case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
          case "application/vnd.oasis.opendocument.spreadsheet":
            label = t("fichier_tableur");
            break;
          case "application/vnd.oasis.opendocument.graphics":
            label = t("fichier_dessin");
            break;
          case "application/vnd.oasis.opendocument.presentation":
          case "application/vnd.ms-powerpoint":
          case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
            label = t("fichier_presentation");
            break;
          case "text/plain":
            label = t("fichier_texte");
            break;
          case "text/markdown":
          case "text/x-markdown":
          case "text/x-web-markdown":
            label = t("fichier_markdown");
            break;
          case "application/json":
            label = t("fichier_json");
            break;
          case "text/xml":
          case "application/xml":
            label = t("fichier_xml");
            break;
          case "text/html":
            label = t("fichier_html");
            break;
          case "text/css":
            label = t("fichier_css");
            break;
          case "text/csv":
            label = t("fichier_csv");
            break;
          case "application/x-javascript":
          case "text/javascript":
            label = t("fichier_javascript");
            break;
          case "text/sql":
            label = t("fichier_sql");
            break;
          case "application/x-sh":
          case "text/x-sh":
            label = t("fichier_shell");
            break;
          case "application/x-yaml":
          case "text/yaml":
          case "text/x-yaml":
            label = t("fichier_yaml");
            break;
          case "text/x-python":
          case "application/x-python":
            label = t("fichier_python");
            break;
          case "text/typescript":
          case "application/typescript":
          case "text/x-typescript":
            label = t("fichier_typescript");
            break;
          case "text/x-php":
          case "application/x-php":
            label = t("fichier_php");
            break;
          case "text/x-java":
          case "text/x-java-source":
            label = t("fichier_java");
            break;
          case "text/x-c":
          case "text/x-c++":
          case "text/x-go":
          case "text/x-rust":
          case "text/x-ruby":
            label = t("fichier_code");
            break;
          case "application/x-ini":
          case "text/x-ini":
          case "application/toml":
          case "text/x-toml":
            label = t("fichier_config");
            break;
          case "text/x-log":
            label = t("fichier_log");
            break;
          default:
            label = t("fichier");
        }
      }
    } else {
      switch (item.nodeType) {
        case "st:site":
          label = t("espace");
          break;
        default:
          label = item.nodeType;
      }
    }
    return label;
  }
}

export default new IconeService();
